import {
  Component,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

/* ==========================================================================
   Avatars — shared initials/image-or-initials logic (was duplicated between
   main.tsx and task-workspace.tsx).
   ========================================================================== */

const nameWords = (value: string) => value.trim().split(/\s+/).filter(Boolean);

// Reveals progressively more of a name at once -- level N = the first N
// characters of BOTH the first and last word for a multi-word name (grown
// together, not just the last name alone, since two people can just as
// easily share a last name and differ only in their first -- growing only
// the last name would never resolve that), or the first N+1 characters of
// the single word itself when there's no last name to fall back on. So
// avatarInitials below can grow a candidate just far enough to stop
// colliding, for any name shape, rather than a single fixed-width rule.
const avatarInitialsAtLevel = (name: string, level: number): string => {
  const parts = nameWords(name);
  if (!parts.length) return "";
  if (level === 0) return parts[0][0];
  const first = parts[0];
  const last = parts.length > 1 ? parts[parts.length - 1] : "";
  return last
    ? first.slice(0, Math.min(level, first.length)) + last.slice(0, Math.min(level, last.length))
    : first.slice(0, Math.min(level + 1, first.length));
};

// First letter of the first name by default. Previously (a) a single-word
// name was left as a bare, un-disambiguated initial even when it collided
// with others -- "nothing to append" -- which is exactly wrong for real
// single-word display names ("Alice"/"Aaron"/"Amit" all rendering plain
// "A"), and (b) even a multi-word name only ever appended a single fixed
// last-name initial, so three people sharing both a first AND last initial
// ("Alice Jones"/"Aaron Jones"/"Amit Jones") still fully collided at "AJ".
// Now: escalate one level at a time (see avatarInitialsAtLevel) only while
// this name's candidate at the current level still matches another roster
// member's candidate at that SAME level -- so a name with nothing to
// disambiguate against stays a single letter, and a name that needs more to
// stand out keeps revealing more of itself until it does, however many
// letters that takes. Called with no roster (or an empty one) -- e.g. the
// nav's own signed-in-user avatar, which has no one to collide with by
// definition -- this always returns just the first initial.
export const avatarInitials = (name: string, allNames: string[] = []): string => {
  if (!nameWords(name).length) return "";
  const others = allNames.filter((other) => other !== name);
  const MAX_LEVEL = 8;
  let level = 0;
  let candidate = avatarInitialsAtLevel(name, level);
  while (
    level < MAX_LEVEL
    && others.some((other) => avatarInitialsAtLevel(other, level).toLowerCase() === candidate.toLowerCase())
  ) {
    const next = avatarInitialsAtLevel(name, level + 1);
    if (next.toLowerCase() === candidate.toLowerCase()) break; // no more characters left to reveal
    level += 1;
    candidate = next;
  }
  return candidate.toUpperCase();
};

export function AvatarContent({
  allNames,
  avatarUrl,
  name,
}: {
  allNames?: string[];
  avatarUrl?: string | null;
  name: string;
}) {
  return avatarUrl ? (
    <img alt="" referrerPolicy="no-referrer" src={avatarUrl} />
  ) : (
    <>{avatarInitials(name, allNames)}</>
  );
}

/* ==========================================================================
   Error boundary — prevents an uncaught render error from white-screening
   the whole app.
   ========================================================================== */

export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error("Taskei render error", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="app-crash" role="alert">
          <p className="eyebrow">Taskei</p>
          <h1>Something went wrong</h1>
          <p>
            An unexpected error stopped this page from working. Reloading
            usually fixes it; if it keeps happening, let your team know.
          </p>
          <button
            className="button primary"
            onClick={() => window.location.reload()}
            type="button"
          >
            Reload Taskei
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ==========================================================================
   Modal accessibility — Escape to close, focus trapped inside while open,
   focus returned to the trigger on close. Shared by every dialog/drawer in
   the app instead of each one reimplementing (or omitting) it.

   Dialog components in this app mount only while open and unmount on close,
   so "on mount" == "on open" and "on unmount" == "on close" — the effect
   below can safely run once per mount with an empty dependency array. A
   module-level stack ensures that when dialogs are nested (e.g. a confirm
   dialog opened from within a task drawer), only the topmost one responds
   to Escape/Tab, and each restores focus to whatever was focused before it
   opened.
   ========================================================================== */

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let modalStack: symbol[] = [];

export function useModalA11y<T extends HTMLElement>(onClose: () => void) {
  const containerRef = useRef<T | null>(null);
  const idRef = useRef(Symbol("modal"));
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const id = idRef.current;
    modalStack.push(id);
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusableElements = () =>
      containerRef.current
        ? Array.from(
            containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
          )
        : [];

    // Respect an element that already grabbed focus on mount (e.g. an
    // <input autoFocus>, which React focuses in a layout effect that runs
    // before this passive effect) instead of stealing it back.
    if (!containerRef.current?.contains(document.activeElement)) {
      const initialFocus = focusableElements()[0] ?? containerRef.current;
      initialFocus?.focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (modalStack[modalStack.length - 1] !== id) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key === "Tab") {
        const items = focusableElements();
        if (items.length === 0) {
          event.preventDefault();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      modalStack = modalStack.filter((entry) => entry !== id);
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, []);

  return containerRef;
}

/* ==========================================================================
   Click-outside-to-close — for lightweight inline dropdowns (FieldPicker,
   the issue-type picker, the reviewer picker) as opposed to useModalA11y's
   full dialogs above.

   These used to close on click-outside via a literal `position: fixed;
   inset: 0` "backdrop" div rendered while open (see .type-picker-backdrop in
   styles.css). That div sits on top of the ENTIRE page, not just the
   dropdown's own area, so while any one of these was left open, a single
   click ANYWHERE else in the app -- another field's trigger, a nav link, a
   board card -- landed on the backdrop instead of its actual target: it
   closed the dropdown but did nothing else, silently requiring a second
   click to accomplish anything. The same full-viewport div also broke mouse
   wheel scrolling while a dropdown was left open scrolling over the task
   drawer landed on the (non-scrolling) backdrop and chained straight past
   the drawer to the board underneath, instead of scrolling the drawer.

   This hook replaces that div with a real "click outside this element"
   listener, so the underlying click/scroll still reaches its actual target
   -- the dropdown merely also closes as a side effect, exactly like it
   already does for the dialogs above.
   ========================================================================== */

// The actual "was this click outside the dropdown" decision, pulled out of
// the hook below as a plain function so it's unit-testable without a DOM
// environment (this repo's unit tests run under `environment: "node"`, no
// jsdom) -- a fake object with just a `.contains()` method stands in for a
// real container element. `null` (not yet mounted, or the dropdown just
// closed) never counts as "outside" -- nothing to compare against yet.
export const isOutsideClick = (container: Pick<Node, "contains"> | null, target: Node): boolean =>
  container !== null && !container.contains(target);

export function useClickOutsideToClose<T extends HTMLElement>(open: boolean, onOutside: () => void) {
  const ref = useRef<T | null>(null);
  const onOutsideRef = useRef(onOutside);
  onOutsideRef.current = onOutside;

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (isOutsideClick(ref.current, event.target as Node)) onOutsideRef.current();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  return ref;
}

/* ==========================================================================
   Error banner — a drop-in replacement for ad hoc
   `{error && <p className="banner-error">{error}</p>}` blocks that always
   carries role="alert" so screen readers hear it.
   ========================================================================== */

export function ErrorBanner({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  if (!children) return null;
  return (
    <p className={`banner-error${className ? ` ${className}` : ""}`} role="alert">
      {children}
    </p>
  );
}

/* ==========================================================================
   Confirm dialog — an on-brand, Promise-based replacement for
   window.confirm()/window.prompt(). Usage:

     const confirm = useConfirm();
     const ok = await confirm({ title: "...", description: "...", tone: "danger" });
     if (!ok) return;

   For the "type the name to confirm" pattern (previously window.prompt),
   pass `requireText`.
   ========================================================================== */

export type ConfirmOptions = {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
  requireText?: string;
  requireTextLabel?: string;
};

type ConfirmState = ConfirmOptions & { resolve: (value: boolean) => void };

const ConfirmContext = createContext<
  ((options: ConfirmOptions) => Promise<boolean>) | null
>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmState | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({ ...options, resolve });
    });
  }, []);

  const settle = (value: boolean) => {
    setState((current) => {
      current?.resolve(value);
      return null;
    });
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && <ConfirmDialogView onClose={settle} state={state} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error("useConfirm must be used within ConfirmProvider");
  return context;
}

function ConfirmDialogView({
  onClose,
  state,
}: {
  onClose: (value: boolean) => void;
  state: ConfirmState;
}) {
  const [typed, setTyped] = useState("");
  const containerRef = useModalA11y<HTMLElement>(() => onClose(false));
  const requiresText = state.requireText !== undefined;
  const canConfirm = !requiresText || typed === state.requireText;

  return (
    <div className="dialog-backdrop" onMouseDown={() => onClose(false)} role="presentation">
      <section
        aria-labelledby="confirm-dialog-title"
        aria-modal="true"
        className={`dialog confirm-dialog${state.tone === "danger" ? " confirm-dialog-danger" : ""}`}
        onMouseDown={(event) => event.stopPropagation()}
        ref={containerRef}
        role="dialog"
      >
        <button className="dialog-close" onClick={() => onClose(false)} type="button">
          ×
        </button>
        <p className="eyebrow">{state.tone === "danger" ? "Destructive action" : "Confirm"}</p>
        <h2 id="confirm-dialog-title">{state.title}</h2>
        {state.description && (
          <p className="confirm-dialog-description">{state.description}</p>
        )}
        {requiresText && (
          <label className="confirm-dialog-type-check">
            {state.requireTextLabel ?? `Type "${state.requireText}" to confirm`}
            <input
              autoComplete="off"
              autoFocus
              onChange={(event) => setTyped(event.target.value)}
              spellCheck={false}
              value={typed}
            />
          </label>
        )}
        <div className="dialog-actions">
          <button className="button secondary" onClick={() => onClose(false)} type="button">
            {state.cancelLabel ?? "Cancel"}
          </button>
          <button
            autoFocus={!requiresText}
            className={`button ${state.tone === "danger" ? "danger" : "primary"}`}
            disabled={!canConfirm}
            onClick={() => onClose(true)}
            type="button"
          >
            {state.confirmLabel ?? "Confirm"}
          </button>
        </div>
      </section>
    </div>
  );
}

/* ==========================================================================
   Toasts — lightweight, auto-dismissing, non-blocking feedback for actions
   (the app previously had no in-app notification system at all: feedback
   was either a persistent layout-shifting banner, or nothing).
   ========================================================================== */

export type ToastTone = "success" | "error" | "info";
type ToastItem = { id: number; message: string; tone: ToastTone };

const ToastContext = createContext<
  ((message: string, tone?: ToastTone) => void) | null
>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const showToast = useCallback((message: string, tone: ToastTone = "success") => {
    const id = ++nextId.current;
    setToasts((items) => [...items, { id, message, tone }]);
    window.setTimeout(() => {
      setToasts((items) => items.filter((item) => item.id !== id));
    }, 4000);
  }, []);

  const dismiss = (id: number) => setToasts((items) => items.filter((item) => item.id !== id));

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      <div aria-live="polite" className="toast-region">
        {toasts.map((toast) => (
          <p
            className={`toast toast-${toast.tone}`}
            key={toast.id}
            onClick={() => dismiss(toast.id)}
          >
            {toast.message}
          </p>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
}

/* ==========================================================================
   Required-field indicator — a visible convention to pair with the
   `required` attribute so mandatory fields are scannable, not just
   discoverable by submitting the form.
   ========================================================================== */

export function RequiredMark() {
  return (
    <span aria-hidden="true" className="required-mark">
      {" "}
      *
    </span>
  );
}

/* ==========================================================================
   Loading skeletons — replace plain "Loading…" text with a shape that hints
   at the content underneath, while keeping an accessible text equivalent
   for screen readers.
   ========================================================================== */

/* ==========================================================================
   Theme — light/dark toggle. Defaults to the OS preference (handled purely
   in CSS via `prefers-color-scheme`); an explicit choice here is persisted
   and always wins over the OS setting. A blocking inline script in
   index.html applies any stored choice before first paint to avoid a flash.
   ========================================================================== */

const THEME_STORAGE_KEY = "taskei-theme";
type Theme = "light" | "dark";

const systemPrefersDark = () => window.matchMedia("(prefers-color-scheme: dark)").matches;

const readStoredTheme = (): Theme | null => {
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored === "light" || stored === "dark" ? stored : null;
};

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme() ?? (systemPrefersDark() ? "dark" : "light"));

  // Bootstrap 5.3 does its own theming off `data-bs-theme`, which is separate
  // from this app's `data-theme`/--jira-* tokens. Without this the two disagree
  // in dark mode: the page background goes dark while every Bootstrap utility
  // stays light, so `.text-body-secondary` renders rgba(33,37,41,.75) on a
  // rgb(26,31,36) background -- black on black, and effectively invisible. It
  // is set here rather than in index.html's pre-paint script because that
  // script's exact text is pinned by the CSP hash in src/worker.ts.
  useEffect(() => {
    document.documentElement.dataset.bsTheme = theme;
  }, [theme]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemChange = () => {
      if (readStoredTheme()) return;
      setTheme(media.matches ? "dark" : "light");
    };
    media.addEventListener("change", handleSystemChange);
    return () => media.removeEventListener("change", handleSystemChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === "dark" ? "light" : "dark";
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
      document.documentElement.dataset.theme = next;
      return next;
    });
  }, []);

  return { theme, toggleTheme };
}

export function Skeleton({
  label,
  variant = "list",
}: {
  label: string;
  variant?: "list" | "board";
}) {
  return (
    <div className={`skeleton skeleton-${variant}`}>
      <span className="sr-only">{label}</span>
      {variant === "board" ? (
        <div aria-hidden="true" className="skeleton-columns">
          {[0, 1, 2, 3].map((column) => (
            <div className="skeleton-column" key={column}>
              <span className="skeleton-line skeleton-line-title" />
              {[0, 1, 2].map((card) => (
                <span className="skeleton-card" key={card} />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div aria-hidden="true" className="skeleton-rows">
          {[0, 1, 2, 3, 4].map((row) => (
            <span className="skeleton-line" key={row} />
          ))}
        </div>
      )}
    </div>
  );
}
